import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "backdrop", "modal", "searchInput", "results", "quickActions", "navigationItems", "helpItems", "actionItem", "searchResults", "searchResultsList", "noResults", "quickActionsSection", "navigationSection", "helpSection"]
  static values = { 
    allowBackgroundClose: Boolean, 
    closeOnEsc: Boolean 
  }

  connect() {
    this.isOpen = false
    this.selectedIndex = -1
    this.filteredItems = []
    this.searchTimeout = null
    
    // Bind the global keydown handler to preserve context
    this.boundHandleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    
    // Keyboard shortcut listener
    document.addEventListener('keydown', this.boundHandleGlobalKeydown)
    
    // Listen for quick access events
    this.boundHandleQuickAccessOpen = this.handleQuickAccessOpen.bind(this)
    this.element.addEventListener('quick-access:open', this.boundHandleQuickAccessOpen)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleGlobalKeydown)
    this.element.removeEventListener('quick-access:open', this.boundHandleQuickAccessOpen)
  }

  handleQuickAccessOpen() {
    this.open()
  }


  handleGlobalKeydown(event) {
    // ALT+Q to open
    if (event.altKey && event.key === 'q') {
      event.preventDefault()
      this.open()
    }
    
    // ESC to close when open
    if (this.isOpen && event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      this.close()
    }
  }

  open() {
    if (this.isOpen) return
    
    this.isOpen = true
    this.containerTarget.classList.remove('hidden')
    this.searchInputTarget.value = ''
    this.search('')
    
    // Add body scroll lock
    document.body.style.overflow = 'hidden'
    
    // Animate modal in
    requestAnimationFrame(() => {
      this.modalTarget.style.transform = 'scale(0.95)'
      this.modalTarget.style.opacity = '0'
      this.backdropTarget.style.opacity = '0'
      
      requestAnimationFrame(() => {
        this.modalTarget.style.transform = 'scale(1)'
        this.modalTarget.style.opacity = '1'
        this.backdropTarget.style.opacity = '1'
        
        // Focus and select all text in search input after animation
        this.searchInputTarget.focus()
        this.searchInputTarget.select()
      })
    })
  }

  close() {
    if (!this.isOpen) return
    
    this.isOpen = false
    this.selectedIndex = -1
    
    // Animate modal out
    this.modalTarget.style.transform = 'scale(0.95)'
    this.modalTarget.style.opacity = '0'
    this.backdropTarget.style.opacity = '0'
    
    setTimeout(() => {
      this.containerTarget.classList.add('hidden')
      // Remove body scroll lock
      document.body.style.overflow = ''
      
      // Reset search
      this.searchInputTarget.value = ''
      this.search('')
      
      // Remove focus from search input
      this.searchInputTarget.blur()
      
      // Dispatch custom event for other controllers
      document.dispatchEvent(new CustomEvent('command-palette:closed'))
    }, 150)
  }

  closeBackground(event) {
    if (this.allowBackgroundCloseValue && event.target === this.containerTarget) {
      this.close()
    }
  }


  search(event) {
    const query = event ? event.target.value.toLowerCase() : ''
    this.selectedIndex = -1
    
    // Clear previous search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    if (query === '') {
      this.showAllSections()
      this.hideSearchResults()
      return
    }
    
    // Search in static sections
    this.hideAllSections()
    this.filteredItems = []
    
    // Search in quick actions
    this.searchInSection(this.quickActionsTarget, query, 'quickActions')
    
    // Search in navigation
    this.searchInSection(this.navigationItemsTarget, query, 'navigation')
    
    // Search in help
    this.searchInSection(this.helpItemsTarget, query, 'help')
    
    // Perform backend search with debounce (300ms delay)
    this.searchTimeout = setTimeout(() => {
      this.performBackendSearch(query)
    }, 300)
    
    // Show results container
    this.resultsTarget.classList.remove('hidden')
  }

  searchInSection(section, query, sectionName) {
    if (!section) return
    
    const items = section.querySelectorAll('[data-command-palette-target="actionItem"]')
    let hasMatches = false
    
    items.forEach((item, index) => {
      const title = item.querySelector('.text-sm.font-medium')?.textContent.toLowerCase() || ''
      const description = item.querySelector('.text-xs.text-gray-500')?.textContent.toLowerCase() || ''
      
      if (title.includes(query) || description.includes(query)) {
        item.classList.remove('hidden')
        this.filteredItems.push({
          element: item,
          section: sectionName,
          index: this.filteredItems.length
        })
        hasMatches = true
      } else {
        item.classList.add('hidden')
      }
    })
    
    // Show section header if there are matches
    if (hasMatches) {
      const sectionHeader = section.previousElementSibling
      if (sectionHeader) {
        sectionHeader.classList.remove('hidden')
        section.classList.remove('hidden')
      }
    }
  }

  showAllSections() {
    // Hide search results when showing all sections
    this.hideSearchResults()
    
    // Show all sections
    const sectionTargets = [this.quickActionsSectionTarget, this.navigationSectionTarget, this.helpSectionTarget]
    sectionTargets.forEach(section => {
      if (section) {
        section.classList.remove('hidden')
        
        // Show all items in section
        const items = section.querySelectorAll('[data-command-palette-target="actionItem"]')
        items.forEach(item => item.classList.remove('hidden'))
      }
    })
    
    this.resultsTarget.classList.remove('hidden')
  }

  hideAllSections() {
    const sectionTargets = [this.quickActionsSectionTarget, this.navigationSectionTarget, this.helpSectionTarget]
    sectionTargets.forEach(section => {
      if (section) {
        section.classList.add('hidden')
      }
    })
  }

  handleKeydown(event) {
    if (!this.isOpen) return
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.navigateDown()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.navigateUp()
        break
      case 'Enter':
        event.preventDefault()
        this.selectCurrentItem()
        break
      case 'Escape':
        event.preventDefault()
        this.close()
        break
    }
  }

  navigateDown() {
    if (this.filteredItems.length === 0) return
    
    this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredItems.length - 1)
    this.updateSelection()
  }

  navigateUp() {
    if (this.filteredItems.length === 0) return
    
    this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
    this.updateSelection()
  }

  updateSelection() {
    // Remove previous selection
    this.filteredItems.forEach(item => {
      item.element.classList.remove('bg-blue-50', 'text-blue-700')
      item.element.classList.add('hover:bg-gray-50', 'hover:text-gray-700')
    })
    
    // Add selection to current item
    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
      const currentItem = this.filteredItems[this.selectedIndex]
      currentItem.element.classList.add('bg-blue-50', 'text-blue-700')
      currentItem.element.classList.remove('hover:bg-gray-50', 'hover:text-gray-700')
      
      // Scroll into view
      currentItem.element.scrollIntoView({ block: 'nearest' })
    }
  }

  selectCurrentItem() {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredItems.length) {
      const currentItem = this.filteredItems[this.selectedIndex]
      const link = currentItem.element.querySelector('a') || currentItem.element
      if (link && link.href) {
        window.location.href = link.href
      } else {
        currentItem.element.click()
      }
    }
  }

  async performBackendSearch(query) {
    if (!query || query.length < 2) {
      this.hideSearchResults()
      return
    }

    try {
      // Search in applicants, talents and contacts
      const [applicants, talents, contacts] = await Promise.all([
        this.searchModel('applicants', query),
        this.searchModel('talents', query),
        this.searchModel('contacts', query)
      ])

      this.displaySearchResults(applicants, talents, contacts, query)
    } catch (error) {
      console.error('Search error:', error)
      this.hideSearchResults()
    }
  }

  async searchModel(model, term) {
    const response = await fetch(`/backend/search?model=${model}&term=${encodeURIComponent(term)}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    
    if (!response.ok) {
      throw new Error(`Search failed for ${model}`)
    }
    
    return response.json()
  }

  displaySearchResults(applicants, talents, contacts, query) {
    const hasResults = applicants.length > 0 || talents.length > 0 || contacts.length > 0

    if (!hasResults) {
      this.showNoResults()
      return
    }

    // Clear previous results
    this.searchResultsListTarget.innerHTML = ''

    // Add applicants
    applicants.forEach(applicant => {
      const item = this.createSearchResultItem(
        applicant,
        `/backend/applicants/${applicant.id}`,
        'user',
        'blue',
        'Bewerber'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    // Add talents
    talents.forEach(talent => {
      const item = this.createSearchResultItem(
        talent,
        `/backend/talents/${talent.id}`,
        'star',
        'green',
        'Talent'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    // Add contacts
    contacts.forEach(contact => {
      const item = this.createSearchResultItem(
        contact,
        `/backend/contacts/${contact.id}`,
        'address-book',
        'purple',
        'Kontakt'
      )
      this.searchResultsListTarget.appendChild(item)
      this.filteredItems.push({ element: item, section: 'searchResults', index: this.filteredItems.length })
    })

    this.showSearchResults()
  }

  createSearchResultItem(record, url, icon, color, type) {
    const item = document.createElement('a')
    item.href = url
    item.className = `group flex items-center px-3 py-2 rounded-lg hover:bg-${color}-50 hover:text-${color}-700 transition-colors duration-150 cursor-pointer`
    item.setAttribute('data-command-palette-target', 'actionItem')

    const colorClasses = {
      blue: 'text-blue-400 group-hover:text-blue-500',
      green: 'text-green-400 group-hover:text-green-500',
      purple: 'text-purple-400 group-hover:text-purple-500'
    }

    item.innerHTML = `
      <div class="flex-shrink-0 mr-3">
        <i class="fas fa-${icon} h-5 w-5 ${colorClasses[color]}"></i>
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-medium truncate">${this.escapeHtml(record.text)}</div>
        <div class="text-xs text-gray-500 truncate">${record.subtext || ''}</div>
      </div>
      <div class="flex-shrink-0 ml-2">
        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${color}-100 text-${color}-800">
          ${type}
        </span>
      </div>
    `

    return item
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  showSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden')
    }
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  hideSearchResults() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.add('hidden')
    }
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.add('hidden')
    }
  }

  showNoResults() {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.remove('hidden')
    }
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.classList.remove('hidden')
    }
    this.searchResultsListTarget.innerHTML = ''
  }
}
